import React from 'react';
import { useBrand } from '../../../app/config/brand';
import { Icons, IconWrapper } from '../../icons/Icons';

interface ServiceSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectService: (serviceId: string) => void;
}

const serviceIcons: Record<string, React.FC<{ className?: string; style?: React.CSSProperties }>> = {
  'e-filing': Icons.EService,
  'court-filing': Icons.CourtFiling,
  'process-serving': Icons.ProcessServing,
  'document-retrieval': Icons.Document,
  'courtesy-copy-delivery': Icons.Delivery,
  'secretary-of-state-filing': Icons.Building,
  'county-recording': Icons.Document,
  'skip-trace': Icons.Locator,
  'eservice': Icons.EService,
};

export const ServiceSelectionModal: React.FC<ServiceSelectionModalProps> = ({ isOpen, onClose, onSelectService }) => {
  const brand = useBrand();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        role="button"
        tabIndex={0}
        aria-label="Close modal"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose(); } }}
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        style={{ backgroundColor: brand.colors.bgCard }}
      >
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold" style={{ color: brand.colors.secondary }}>
            Place New Order
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors min-h-touch min-w-touch flex items-center justify-center"
          >
            <Icons.X className="w-5 h-5" style={{ color: brand.colors.textLight }} />
          </button>
        </div>

        <p className="text-sm mb-4 sm:mb-6" style={{ color: brand.colors.textLight }}>
          Select the type of service you need:
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {brand.navigation.services.map(service => {
            const ServiceIcon = serviceIcons[service.id] || Icons.Document;
            return (
              <button
                key={service.id}
                onClick={() => {
                  onSelectService(service.id);
                  onClose();
                }}
                className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border-2 transition-all hover:border-blue-400 hover:bg-blue-50/50 text-left min-h-touch"
                style={{ borderColor: brand.colors.border }}
              >
                <IconWrapper size="md">
                  <ServiceIcon className="w-6 h-6" style={{ color: brand.colors.link }} />
                </IconWrapper>
                <div>
                  <div className="font-medium" style={{ color: brand.colors.secondary }}>
                    {service.label}
                  </div>
                  <div className="text-sm mt-1" style={{ color: brand.colors.textLight }}>
                    {service.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
